import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, money, formatApiError } from "../lib/api";
import { Card, Badge, PrimaryButton, Modal, Field, inputClass } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkle, CalendarBlank, Warning, ShieldWarning, CheckCircle,
  FilePdf, ArrowClockwise, ListChecks, SealCheck, Gauge, Plus, Trash, CurrencyDollar, PencilSimple,
} from "@phosphor-icons/react";
import { SignaturePad } from "../components/SignaturePad";

const NIVEL = {
  alto: { cls: "bg-emerald-100 text-emerald-700" },
  medio: { cls: "bg-amber-100 text-amber-700" },
  bajo: { cls: "bg-red-100 text-red-700" },
};
const ESTADO = {
  cumplido: { dot: "bg-emerald-500", text: "text-emerald-600", label: "Cumplido" },
  parcial: { dot: "bg-amber-500", text: "text-amber-600", label: "Parcial" },
  no_cumplido: { dot: "bg-red-500", text: "text-red-600", label: "No cumplido" },
  sin_evidencia: { dot: "bg-slate-400", text: "text-muted-foreground", label: "Sin evidencia" },
};

function AiList({ title, items, icon: Icon, tone = "text-foreground" }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4">
      <div className={`flex items-center gap-2 text-sm font-semibold mb-2 ${tone}`}><Icon size={16} weight="bold" /> {title}</div>
      <ul className="space-y-1.5 pl-1">
        {items.map((t, i) => <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-primary">›</span><span>{t}</span></li>)}
      </ul>
    </div>
  );
}

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = true;
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const [c, setC] = useState(null);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [sig, setSig] = useState(null);
  const [signing, setSigning] = useState(false);
  const [checking, setChecking] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ests, setEsts] = useState([]);
  const [coOpen, setCoOpen] = useState(false);
  const [coForm, setCoForm] = useState({ reason: "", amount_delta: "0", new_end_date: "", description: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", start_date: "", end_date: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [usingAmount, setUsingAmount] = useState(false);
  const canApproveCO = ["admin", "gerente_general"].includes(user?.role);

  const useAiAmount = async () => {
    if (!window.confirm("Se tomará el monto detectado en el documento como monto del contrato (los adendums aprobados se sumarán/restarán sobre ese monto). ¿Continuar?")) return;
    setUsingAmount(true);
    try {
      await api.post(`/contracts/${id}/use-ai-amount`);
      toast.success("Monto actualizado desde el documento"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUsingAmount(false); }
  };

  const openEdit = () => {
    setEditForm({ title: c.title || "", description: c.description || "", start_date: c.start_date || "", end_date: c.end_date || "" });
    setEditOpen(true);
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.title.trim()) return toast.error("El título no puede estar vacío");
    setSavingEdit(true);
    try {
      await api.put(`/contracts/${id}`, editForm);
      toast.success("Contrato actualizado"); setEditOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  const signContract = async () => {
    if (!sig) return toast.error("Firma primero");
    setSigning(true);
    const fd = new FormData(); fd.append("signature", sig);
    try { await api.post(`/contracts/${id}/sign`, fd); toast.success("Contrato firmado"); setSig(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSigning(false); }
  };

  const load = () => api.get(`/contracts/${id}`).then((r) => setC(r.data)).catch(() => navigate("/contratos"));
  const loadOrders = () => api.get(`/change-orders?contract_id=${id}`).then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); loadOrders(); api.get(`/estimations?contract_id=${id}`).then((r) => setEsts(r.data)).catch(() => {}); }, [id]);

  // Auto-refresca mientras la IA analiza el contrato en segundo plano
  useEffect(() => {
    if (c?.ai_analysis?.status !== "procesando") return;
    const t = setInterval(() => load(), 5000);
    return () => clearInterval(t);
  }, [c?.ai_analysis?.status]);

  const createCO = async (e) => {
    e.preventDefault();
    try {
      await api.post("/change-orders", { contract_id: id, reason: coForm.reason,
        amount_delta: parseFloat(coForm.amount_delta || 0), new_end_date: coForm.new_end_date || null, description: coForm.description });
      toast.success("Adendum registrado");
      setCoOpen(false); setCoForm({ reason: "", amount_delta: "0", new_end_date: "", description: "" }); loadOrders();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const resolveCO = async (oid, action) => {
    const fd = new FormData(); fd.append("action", action);
    try { await api.post(`/change-orders/${oid}/resolve`, fd); toast.success(action === "approve" ? "Adendum aprobado" : "Adendum rechazado"); load(); loadOrders(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reanalyze = async () => {
    setReanalyzing(true);
    try { await api.post(`/contracts/${id}/reanalyze`); toast.success("Re-analizado con IA"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setReanalyzing(false); }
  };

  const runCompliance = async () => {
    setChecking(true);
    try { await api.post(`/contracts/${id}/compliance`); toast.success("Cumplimiento evaluado con IA"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  const delContract = async () => {
    if (!window.confirm("¿Eliminar este contrato y sus estimaciones? Quedará registrado en Eliminados.")) return;
    try { await api.delete(`/contracts/${id}`); toast.success("Contrato eliminado"); navigate("/contratos"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!c) return <div className="text-muted-foreground">Cargando…</div>;
  const ai = c.ai_analysis;
  const comp = c.compliance_analysis;

  return (
    <div>
      <button onClick={() => navigate("/contratos")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={16} /> Volver a contratos
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-1">{c.contractor_name}</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">{c.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge status={c.status} />
          <button onClick={openEdit} data-testid="edit-contract-btn" title="Editar título del contrato" className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-sm"><PencilSimple size={20} /></button>
          {canDelete && <button onClick={delContract} data-testid="delete-contract-btn" title="Eliminar contrato" className="p-2 text-danger hover:bg-red-50 rounded-sm"><Trash size={20} /></button>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-5">
          <div className="overline">Monto</div>
          <div className="font-heading font-extrabold text-2xl text-primary mt-1">{money(c.amount)}</div>
          {c.anticipo_percent > 0 && <div className="text-xs text-muted-foreground mt-1">Anticipo {c.anticipo_percent}% · {money(c.anticipo_amount)}</div>}
          {c.retencion_percent > 0 && <div className="text-xs text-amber-600 mt-0.5">Fondo de garantía {c.retencion_percent}%</div>}
          {c.amount_summary && c.amount_summary.total_delta !== 0 && (
            <div className="text-xs text-muted-foreground mt-1" data-testid="amount-original">Original {money(c.amount_summary.original)} · Ajustes <b className={c.amount_summary.total_delta > 0 ? "text-emerald-600" : "text-red-600"}>{c.amount_summary.total_delta > 0 ? "+" : ""}{money(c.amount_summary.total_delta)}</b></div>
          )}
        </Card>
        <Card className="p-5"><div className="overline">Inicio</div><div className="font-heading font-bold text-lg mt-1">{c.start_date || "—"}</div></Card>
        <Card className="p-5"><div className="overline">Término</div><div className="font-heading font-bold text-lg mt-1">{c.end_date || "—"}</div></Card>
        <Card className="p-5">
          <div className="overline">Contrato PDF</div>
          {c.pdf_path ? (
            <a href={fileUrl(c.pdf_path)} target="_blank" rel="noreferrer" data-testid="view-pdf-link" className="inline-flex items-center gap-1 text-primary font-semibold mt-1 hover:underline"><FilePdf size={18} weight="fill" /> Abrir</a>
          ) : <div className="text-muted-foreground mt-1">Sin PDF</div>}
        </Card>
      </div>

      {ests.length > 0 && (
        <Card className="p-6 mb-6" data-testid="avance-historial">
          <div className="overline mb-3">Historial de avance (estimaciones)</div>
          <div className="space-y-2">
            {(() => {
              const amt = c.amount || 0; let acc = 0;
              return [...ests].sort((a, b) => (a.number || 0) - (b.number || 0)).map((e) => {
                const canceled = ["cancelado", "suspendido"].includes(e.status);
                if (!canceled) acc += (e.amount_requested || 0);
                const pct = amt > 0 ? Math.round((acc / amt) * 100) : 0;
                return (
                  <div key={e.id} className="flex items-center gap-3 text-sm border-b border-border/60 pb-2 last:border-0" data-testid={`avance-row-${e.id}`}>
                    <span className="w-8 font-bold text-primary">#{e.number}</span>
                    <span className="w-28 text-muted-foreground truncate">{e.period || (e.created_at || "").slice(0, 10)}</span>
                    <span className="w-32 font-semibold">{money(e.amount_requested)}</span>
                    <div className="flex-1 h-2 bg-muted rounded-sm overflow-hidden"><div className={`h-full ${canceled ? "bg-slate-300" : "bg-primary"}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
                    <span className="w-24 text-right text-xs">{canceled ? <em className="text-muted-foreground">{e.status}</em> : `Acum. ${pct}%`}</span>
                  </div>
                );
              });
            })()}
          </div>
        </Card>
      )}

      {c.description && <Card className="p-6 mb-6"><div className="overline mb-2">Descripción</div><p className="text-sm">{c.description}</p></Card>}

      {/* Aceptación / firma del contrato */}
      <Card className="p-6 mb-6">
        <div className="overline mb-3">Aceptación del contrato</div>
        {c.acceptance_signature_path ? (
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-1.5 text-success font-semibold" data-testid="contract-accepted"><SealCheck size={20} weight="fill" /> Aceptado por {c.accepted_by}</span>
            <span className="text-xs text-muted-foreground">{c.accepted_at && new Date(c.accepted_at).toLocaleString("es-MX")}</span>
            <img src={fileUrl(c.acceptance_signature_path)} alt="firma" className="h-14 border border-border rounded-sm bg-white ml-auto" />
          </div>
        ) : (
          <div className="max-w-md">
            <p className="text-sm text-muted-foreground mb-2">El contratista (o un responsable) puede firmar la aceptación de este contrato.</p>
            <SignaturePad onChange={setSig} />
            <button onClick={signContract} disabled={signing} data-testid="sign-contract-btn"
              className="mt-2 inline-flex items-center gap-2 h-10 px-4 bg-primary text-foreground font-semibold rounded-sm hover:bg-orange-600 transition-colors duration-200 disabled:opacity-60">
              <SealCheck size={18} weight="bold" /> {signing ? "Firmando…" : "Firmar aceptación"}
            </button>
          </div>
        )}
      </Card>

      {/* AI panel */}
      <div className="bg-white border border-border text-foreground rounded-md p-6 lg:p-8 mb-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Sparkle size={22} weight="fill" className="text-primary" />
            <span className="font-heading font-bold text-xl">Análisis de IA del contrato</span>
          </div>
          {isManager && c.pdf_path && (
            <button onClick={reanalyze} disabled={reanalyzing} data-testid="reanalyze-btn"
              className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-foreground border border-border rounded-sm px-3 py-1.5 transition-colors duration-200">
              <ArrowClockwise size={15} weight="bold" className={reanalyzing ? "animate-spin" : ""} /> {reanalyzing ? "Analizando…" : "Re-analizar"}
            </button>
          )}
        </div>

        {!ai ? (
          <p className="text-muted-foreground text-sm">No hay análisis. Sube un PDF del contrato para generarlo.</p>
        ) : ai.status === "procesando" ? (
          <div className="flex items-center gap-3 text-sm text-slate-700" data-testid="ai-processing">
            <ArrowClockwise size={18} weight="bold" className="animate-spin text-primary" />
            La IA está analizando el contrato… esto puede tardar hasta 1 minuto. La página se actualizará sola.
          </div>
        ) : ai.error ? (
          <p className="text-amber-700 text-sm">{ai.error}</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10">
            <div className="lg:col-span-2 mb-5">
              <div className="overline !text-muted-foreground mb-2">Resumen ejecutivo</div>
              <p className="text-foreground leading-relaxed">{ai.resumen}</p>
              {(ai.fecha_inicio || ai.fecha_termino) && (
                <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-700">
                  {ai.fecha_inicio && <span className="inline-flex items-center gap-1.5"><CalendarBlank size={16} /> Inicio detectado: <b className="text-foreground">{ai.fecha_inicio}</b></span>}
                  {ai.fecha_termino && <span className="inline-flex items-center gap-1.5"><CalendarBlank size={16} /> Término detectado: <b className="text-foreground">{ai.fecha_termino}</b></span>}
                </div>
              )}
              {ai.monto_total != null && (
                <div className="mt-2 text-sm text-slate-700 flex flex-wrap items-center gap-2" data-testid="ai-monto">
                  <span className="inline-flex items-center gap-1.5"><CurrencyDollar size={16} /> Monto detectado por IA: <b className="text-foreground">{money(ai.monto_total)}</b></span>
                  {isManager && Math.round((ai.monto_total || 0)) !== Math.round((c.amount_summary?.original ?? c.amount) || 0) && (
                    <button onClick={useAiAmount} disabled={usingAmount} data-testid="use-ai-amount-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-primary hover:bg-orange-600 rounded-sm px-2.5 py-1 transition-colors">
                      {usingAmount ? "Aplicando…" : "Usar este monto del documento"}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div>
              <AiList title="Obligaciones del contratista" items={ai.obligaciones} icon={ListChecks} />
              <AiList title="Entregables a verificar" items={ai.entregables} icon={CheckCircle} />
            </div>
            <div>
              <AiList title="Penalizaciones al contratista" items={ai.penalizaciones_contratista} icon={ShieldWarning} tone="text-red-600" />
              <AiList title="Penalizaciones / responsabilidad del contratante" items={ai.penalizaciones_contratante} icon={ShieldWarning} tone="text-amber-700" />
              <AiList title="Adendums" items={ai.adendums} icon={FilePdf} />
              <AiList title="Alertas y riesgos" items={ai.alertas} icon={Warning} tone="text-amber-700" />
            </div>
          </div>
        )}
      </div>

      {/* Verificación IA de cumplimiento */}
      <div className="bg-white border border-border text-foreground rounded-md p-6 lg:p-8 mb-6" data-testid="compliance-panel">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Gauge size={22} weight="fill" className="text-primary" />
            <span className="font-heading font-bold text-xl">Verificación de cumplimiento (IA)</span>
          </div>
          {isManager && (
            <button onClick={runCompliance} disabled={checking} data-testid="compliance-btn"
              className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-foreground border border-border rounded-sm px-3 py-1.5 transition-colors duration-200">
              <ArrowClockwise size={15} weight="bold" className={checking ? "animate-spin" : ""} /> {checking ? "Evaluando…" : (comp ? "Re-evaluar" : "Evaluar cumplimiento")}
            </button>
          )}
        </div>
        {!comp ? (
          <p className="text-muted-foreground text-sm">Compara lo contratado (entregables y obligaciones) contra el avance real (partidas y fotos) para prevenir sobre-pagos. Presiona “Evaluar cumplimiento”.</p>
        ) : (
          <div>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm text-sm font-bold ${NIVEL[comp.nivel_cumplimiento]?.cls || "bg-muted text-foreground"}`} data-testid="compliance-level">
                Cumplimiento: {comp.nivel_cumplimiento || "—"}
              </span>
              {typeof comp.porcentaje_estimado === "number" && (
                <span className="text-sm text-slate-700">Avance estimado por IA: <b className="text-foreground">{comp.porcentaje_estimado}%</b></span>
              )}
              {comp.generated_at && <span className="text-xs text-muted-foreground ml-auto">{new Date(comp.generated_at).toLocaleString("es-MX")}</span>}
            </div>
            {comp.resumen && <p className="text-foreground leading-relaxed mb-4">{comp.resumen}</p>}
            {comp.recomendacion_pago && (
              <div className="bg-muted border border-border rounded-sm p-4 mb-4">
                <div className="overline !text-muted-foreground mb-1">Recomendación de pago</div>
                <p className="text-sm text-slate-800">{comp.recomendacion_pago}</p>
              </div>
            )}
            {comp.entregables?.length > 0 && (
              <div className="mb-4">
                <div className="overline !text-muted-foreground mb-2">Entregables evaluados</div>
                <div className="space-y-2">
                  {comp.entregables.map((it, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${ESTADO[it.estado]?.dot || "bg-slate-500"}`} />
                      <div>
                        <span className="text-foreground font-medium">{it.nombre}</span>
                        <span className={`ml-2 text-xs ${ESTADO[it.estado]?.text || "text-muted-foreground"}`}>{ESTADO[it.estado]?.label || it.estado}</span>
                        {it.comentario && <p className="text-muted-foreground">{it.comentario}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <AiList title="Riesgos de sobre-pago" items={comp.riesgos_sobrepago} icon={Warning} tone="text-amber-700" />
          </div>
        )}
      </div>

      {/* Órdenes de cambio / Adendums */}
      <Card className="p-6 mb-6" data-testid="change-orders">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ListChecks size={20} weight="fill" className="text-primary" />
            <span className="font-heading font-bold text-xl">Órdenes de cambio / Adendums</span>
          </div>
          {isManager && (
            <button onClick={() => setCoOpen(true)} data-testid="add-co-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
              <Plus size={16} weight="bold" /> Nuevo adendum
            </button>
          )}
        </div>
        {c.amount_summary && (
          <div className="grid grid-cols-3 gap-3 mb-4" data-testid="amount-summary">
            <div className="rounded-sm border border-border p-3"><div className="overline">Monto original</div><div className="font-heading font-bold text-base mt-0.5">{money(c.amount_summary.original)}</div></div>
            <div className="rounded-sm border border-border p-3"><div className="overline">Ajustes (adendums)</div><div className={`font-heading font-bold text-base mt-0.5 ${c.amount_summary.total_delta > 0 ? "text-emerald-600" : c.amount_summary.total_delta < 0 ? "text-red-600" : ""}`}>{c.amount_summary.total_delta > 0 ? "+" : ""}{money(c.amount_summary.total_delta)}</div></div>
            <div className="rounded-sm border border-primary/40 bg-primary/5 p-3"><div className="overline">Monto vigente</div><div className="font-heading font-extrabold text-base mt-0.5 text-primary">{money(c.amount_summary.current)}</div></div>
          </div>
        )}
        {orders.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin órdenes de cambio. Un adendum ajusta el monto y/o la fecha de término del contrato tras su aprobación por el Gerente General.</p>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} data-testid={`co-${o.id}`} className="border border-border rounded-sm p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">Adendum #{o.number} · {o.reason}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">
                      {o.amount_delta ? `${o.amount_delta > 0 ? "Aditiva" : "Deductiva"}: ${o.amount_delta > 0 ? "+" : ""}${money(o.amount_delta)}` : "Sin cambio de monto"}
                      {o.new_end_date ? ` · Nueva fecha término: ${o.new_end_date}` : ""}
                    </div>
                    {o.description && <p className="text-sm mt-1">{o.description}</p>}
                    <div className="text-xs text-muted-foreground mt-1">Creado por {o.created_by}{o.resolved_by ? ` · Resuelto por ${o.resolved_by}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2.5 py-1 rounded-sm text-xs font-semibold ${o.status === "aprobado" ? "bg-emerald-50 text-emerald-700" : o.status === "rechazado" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{o.status}</span>
                    {canApproveCO && o.status === "pendiente" && (
                      <>
                        <button onClick={() => resolveCO(o.id, "approve")} data-testid={`co-approve-${o.id}`} className="text-success" title="Aprobar"><CheckCircle size={20} weight="fill" /></button>
                        <button onClick={() => resolveCO(o.id, "reject")} data-testid={`co-reject-${o.id}`} className="text-danger" title="Rechazar"><ShieldWarning size={20} weight="fill" /></button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal open={coOpen} onClose={() => setCoOpen(false)} title="Nuevo adendum / orden de cambio">
        <form onSubmit={createCO} className="space-y-4">
          <Field label="Motivo *"><input required data-testid="co-reason" className={inputClass} value={coForm.reason} onChange={(e) => setCoForm({ ...coForm, reason: e.target.value })} /></Field>
          <Field label="Ajuste de monto (MXN, puede ser negativo)"><input type="number" step="0.01" data-testid="co-amount" className={inputClass} value={coForm.amount_delta} onChange={(e) => setCoForm({ ...coForm, amount_delta: e.target.value })} /></Field>
          <Field label="Nueva fecha de término (opcional)"><input type="date" className={inputClass} value={coForm.new_end_date} onChange={(e) => setCoForm({ ...coForm, new_end_date: e.target.value })} /></Field>
          <Field label="Descripción"><textarea className={inputClass + " h-24 py-2"} value={coForm.description} onChange={(e) => setCoForm({ ...coForm, description: e.target.value })} /></Field>
          <PrimaryButton type="submit" data-testid="save-co-btn">Registrar adendum</PrimaryButton>
        </form>
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Editar contrato">
        <form onSubmit={saveEdit} className="space-y-4">
          <Field label="Título / nombre del contrato *"><input required data-testid="edit-contract-title" className={inputClass} value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inicio"><input type="date" data-testid="edit-contract-start" className={inputClass} value={editForm.start_date || ""} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} /></Field>
            <Field label="Término"><input type="date" data-testid="edit-contract-end" className={inputClass} value={editForm.end_date || ""} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} /></Field>
          </div>
          <Field label="Descripción"><textarea className={inputClass + " h-24 py-2"} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></Field>
          <PrimaryButton type="submit" disabled={savingEdit} data-testid="save-edit-contract-btn">{savingEdit ? "Guardando…" : "Guardar cambios"}</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
